// =============================================================================
// src/views/workspace/workspace-section.ts — Wiring do Workspace funcional.
//
// Liga o store (workspace-store) ao DOM: renderiza os cards por filtro+ordenação,
//   delega cliques no grid (selecionar/favoritar/excluir + duplo-clique = abrir),
//   liga nav/tags/chips/busca, o modal "Nova análise" (pré-preenchido pela captura
//   seção+zona) e o "Restaurar exemplos". Cada ativação cria uma SESSÃO nova
//   (store/snapshot/undo/filtro) — idempotente em produção (a rota ativa 1×) e
//   isolada em teste (localStorage limpo → reseed).
//
// SRP: só o wiring DOM↔store. Markup em index.ts; dados em store/seed; render puro
//   em card-render/detail-render; navegação em workspace-navigate.
// =============================================================================

import type { AppContext, SectionId, ZonaKey } from '../../app/types';
import type {
  WorkspaceAnalysis,
  WorkspaceCategory,
  WorkspaceSort,
  WorkspaceTag,
} from './workspace-types';
import { byId, qsa } from '../../app/dom';
import { bindOnce, setActiveItem, setSelectedItem } from '../../core/ui';
import { SectionManager } from '../../core/section-manager';
import { MANAUS_ZONA_DISPLAY_NAME } from '../../data/manaus-zones';
import { createWorkspaceStore, type WorkspaceStore } from './workspace-store';
import { WorkspaceMapSnapshotCache } from './workspace-map-snapshot';
import { WorkspaceUndoToast } from './workspace-undo-toast';
import { filterAnalyses, sortAnalyses, countByCategory } from './workspace-filter';
import { renderCardGrid, renderEmptyState } from './workspace-card-render';
import { applyDetailPanel } from './workspace-detail-render';
import { openAnalysisInMap } from './workspace-navigate';
import { WORKSPACE_SECTION_OPTIONS, sectionLabelOf } from './workspace-sections';

/** Categorias da nav (ordem dos itens da sidebar). */
const WORKSPACE_NAV_CATEGORIES: readonly WorkspaceCategory[] = [
  'mine', 'shared', 'team', 'recent', 'favorites',
];

/** Rótulo do título por categoria ativa. */
const WORKSPACE_CATEGORY_LABELS: Record<WorkspaceCategory, string> = {
  mine: 'Minhas análises',
  shared: 'Compartilhadas',
  team: 'Do time',
  recent: 'Recentes',
  favorites: 'Favoritos',
};

/** Seção-destino default quando a seção ativa não é navegável (grafo/cinema/null). */
const DEFAULT_TARGET_SECTION: SectionId = 'mapa-eleitoral';

/** Estado vivo de uma abertura do Workspace. */
interface WorkspaceSession {
  readonly store: WorkspaceStore;
  readonly snapshot: WorkspaceMapSnapshotCache;
  readonly undo: WorkspaceUndoToast;
  filter: { category: WorkspaceCategory; tag: WorkspaceTag | null; sort: WorkspaceSort; search: string };
}

/** Cria uma sessão nova (store lê o localStorage atual → seed se vazio/inválido). */
function createSession(): WorkspaceSession {
  return {
    store: createWorkspaceStore({ now: () => Date.now(), storage: localStorage }),
    snapshot: new WorkspaceMapSnapshotCache(),
    undo: new WorkspaceUndoToast(),
    filter: { category: 'mine', tag: null, sort: 'recent', search: '' },
  };
}

/** Liga todo o comportamento do Workspace (chamado uma vez pela rota ao abrir). */
export function activateWorkspaceSection(ctx: AppContext): void {
  const session = createSession();
  session.snapshot.attach(ctx.bus);
  session.store.subscribe(() => renderWorkspace(ctx, session));
  bindWorkspaceControls(ctx, session);
  renderWorkspace(ctx, session);
}

/** Liga todos os listeners (bindOnce em nós estáveis; delegação no grid). */
function bindWorkspaceControls(ctx: AppContext, session: WorkspaceSession): void {
  const grid = byId('u-ws-card-grid');
  bindOnce(grid, 'click', 'ws-grid', (e) => onGridClick(e, ctx, session));
  bindOnce(grid, 'dblclick', 'ws-grid-open', (e) => onGridDblClick(e, ctx, session));
  bindOnce(byId('u-ws-search'), 'input', 'ws-search', () => onSearch(ctx, session));
  bindOnce(byId('u-ws-new-btn'), 'click', 'ws-new', () => openCreateModal(session));
  bindOnce(byId('u-ws-create-form'), 'submit', 'ws-create', (e) => onCreateSubmit(e, ctx, session));
  bindOnce(byId('u-ws-create-cancel'), 'click', 'ws-cancel', () => closeCreateModal());
  bindOnce(byId('u-ws-open-btn'), 'click', 'ws-open', () => openSelected(ctx, session));
  bindOnce(byId('u-ws-reset-btn'), 'click', 'ws-reset', () => onResetClick(session));
  bindNavItems(ctx, session);
  bindTags(ctx, session);
  bindSortChips(ctx, session);
  bindViewToggle();
}

/** Itens da navegação lateral: trocam a categoria do filtro. */
function bindNavItems(ctx: AppContext, session: WorkspaceSession): void {
  const items = qsa<HTMLElement>('.u-ws-nav-item');
  items.forEach((item) => {
    const category = requiredData(item, 'view') as WorkspaceCategory;
    bindOnce(item, 'click', 'ws-nav', () => onNavClick(category, item, items, ctx, session));
  });
}

/** Tags da sidebar: filtro single-select (toggle). */
function bindTags(ctx: AppContext, session: WorkspaceSession): void {
  qsa<HTMLElement>('.u-ws-tag').forEach((tagEl) => {
    const tag = requiredData(tagEl, 'tag') as WorkspaceTag;
    bindOnce(tagEl, 'click', 'ws-tag', () => onTagClick(tag, ctx, session));
  });
}

/** Chips do topo: ordenação (recent/alpha/views). */
function bindSortChips(ctx: AppContext, session: WorkspaceSession): void {
  const chips = qsa<HTMLElement>('#u-workspace-page .u-chip');
  chips.forEach((chip) => {
    const sort = requiredData(chip, 'sort') as WorkspaceSort;
    bindOnce(chip, 'click', 'ws-sort', () => onSortClick(sort, chip, chips, ctx, session));
  });
}

/** Toggle grid/list: ajusta as colunas (comportamento preservado do mockup). */
function bindViewToggle(): void {
  const buttons = qsa<HTMLElement>('.u-ws-view-toggle button');
  buttons.forEach((button) => {
    bindOnce(button, 'click', 'ws-view', () => onViewToggle(button, buttons));
  });
}

/** Re-renderiza grid + contadores + título + painel de detalhe a partir do estado. */
function renderWorkspace(ctx: AppContext, session: WorkspaceSession): void {
  const all = session.store.listAnalyses();
  const visible = sortAnalyses(filterAnalyses(all, session.filter), session.filter.sort);
  byId('u-ws-card-grid').innerHTML = visible.length
    ? renderCardGrid(visible, session.store.selectedId)
    : renderEmptyState();
  updateNavCounts(all);
  byId('u-ws-title').textContent = WORKSPACE_CATEGORY_LABELS[session.filter.category];
  const selectedId = session.store.selectedId;
  applyDetailPanel(selectedId ? session.store.getAnalysis(selectedId) : null);
}

/** Atualiza os badges numéricos da navegação. */
function updateNavCounts(all: readonly WorkspaceAnalysis[]): void {
  const counts = countByCategory(all);
  WORKSPACE_NAV_CATEGORIES.forEach((category) => {
    byId(`u-ws-count-${category}`).textContent = String(counts[category]);
  });
}

/** Delegação de clique no grid: novo / favoritar / excluir / selecionar. */
function onGridClick(e: Event, ctx: AppContext, session: WorkspaceSession): void {
  const target = e.target as HTMLElement;
  if (target.closest('[data-act="new"]')) {
    openCreateModal(session);
    return;
  }
  const card = target.closest<HTMLElement>('[data-id]');
  if (!card) return;
  const id = requiredData(card, 'id');
  const action = target.closest<HTMLElement>('[data-act]');
  if (action?.dataset.act === 'fav') return session.store.toggleFavorite(id);
  if (action?.dataset.act === 'del') return onDelete(id, session);
  session.store.select(id);
}

/** Duplo-clique no card: seleciona e abre (atalho da restauração). */
function onGridDblClick(e: Event, ctx: AppContext, session: WorkspaceSession): void {
  const card = (e.target as HTMLElement).closest<HTMLElement>('[data-id]');
  if (!card) return;
  session.store.select(requiredData(card, 'id'));
  openSelected(ctx, session);
}

/** Exclui + oferece desfazer (sem confirm() bloqueante). */
function onDelete(id: string, session: WorkspaceSession): void {
  session.store.deleteAnalysis(id);
  session.undo.show('Análise excluída', () => session.store.restoreAnalysis());
}

/** Restaura os exemplos + oferece desfazer. */
function onResetClick(session: WorkspaceSession): void {
  session.store.resetToSeed();
  session.undo.show('Exemplos restaurados', () => session.store.restoreReset());
}

/** Abre a análise selecionada no mapa (botão "Abrir análise" / duplo-clique). */
function openSelected(ctx: AppContext, session: WorkspaceSession): void {
  const selectedId = session.store.selectedId;
  const analysis = selectedId ? session.store.getAnalysis(selectedId) : null;
  if (analysis) openAnalysisInMap(analysis, ctx);
}

/** Busca: atualiza o filtro e re-renderiza. */
function onSearch(ctx: AppContext, session: WorkspaceSession): void {
  const input = byId('u-ws-search') as HTMLInputElement;
  session.filter = { ...session.filter, search: input.value };
  renderWorkspace(ctx, session);
}

/** Nav: marca ativo, repinta e troca a categoria do filtro. */
function onNavClick(
  category: WorkspaceCategory,
  item: HTMLElement,
  items: readonly HTMLElement[],
  ctx: AppContext,
  session: WorkspaceSession,
): void {
  setActiveItem(items, item);
  items.forEach(paintNavItem);
  session.filter = { ...session.filter, category };
  renderWorkspace(ctx, session);
}

/** Tag: alterna single-select e re-renderiza. */
function onTagClick(tag: WorkspaceTag, ctx: AppContext, session: WorkspaceSession): void {
  const next = session.filter.tag === tag ? null : tag;
  session.filter = { ...session.filter, tag: next };
  paintTags(next);
  renderWorkspace(ctx, session);
}

/** Chip de ordenação: marca selecionado e re-renderiza. */
function onSortClick(
  sort: WorkspaceSort,
  chip: HTMLElement,
  chips: readonly HTMLElement[],
  ctx: AppContext,
  session: WorkspaceSession,
): void {
  setSelectedItem(chips, chip);
  session.filter = { ...session.filter, sort };
  renderWorkspace(ctx, session);
}

/** Toggle grid/list: ativa o botão e troca o template de colunas. */
function onViewToggle(button: HTMLElement, buttons: readonly HTMLElement[]): void {
  setActiveItem(buttons, button);
  buttons.forEach(paintViewButton);
  byId('u-ws-card-grid').style.gridTemplateColumns =
    button.dataset.view === 'list' ? '1fr' : '1fr 1fr';
}

/** Abre o modal "Nova análise" pré-preenchido pela captura. */
function openCreateModal(session: WorkspaceSession): void {
  prefillCreateForm(session);
  const modal = byId('u-ws-create-modal');
  modal.classList.remove('hidden');
  modal.setAttribute('aria-hidden', 'false');
}

/** Fecha o modal "Nova análise". */
function closeCreateModal(): void {
  const modal = byId('u-ws-create-modal');
  modal.classList.add('hidden');
  modal.setAttribute('aria-hidden', 'true');
}

/** Pré-preenche o form com seção (SectionManager) + zona (cache de region:select). */
function prefillCreateForm(session: WorkspaceSession): void {
  const form = byId('u-ws-create-form') as HTMLFormElement;
  const zona = session.snapshot.last()?.zona ?? '';
  const active = SectionManager.activeSection;
  const section = active && isNavigableSection(active) ? active : DEFAULT_TARGET_SECTION;
  setControlValue(form, 'zona', zona);
  setControlValue(form, 'targetSection', section);
  setControlValue(form, 'title', suggestTitle(zona, section));
}

/** Submit do modal: cria a análise (defaults honestos), fecha, toast. */
function onCreateSubmit(e: Event, ctx: AppContext, session: WorkspaceSession): void {
  e.preventDefault();
  const form = byId('u-ws-create-form') as HTMLFormElement;
  const title = controlValue(form, 'title').trim();
  if (title === '') return;
  session.store.createAnalysis({
    title,
    tags: checkedTags(form),
    targetSection: controlValue(form, 'targetSection') as SectionId,
    zona: zonaFromValue(controlValue(form, 'zona')),
    year: optionalText(controlValue(form, 'year')),
    layerLabel: optionalText(controlValue(form, 'layerLabel')),
  });
  closeCreateModal();
  form.reset();
  ctx.commands.sendPrompt('Análise criada');
}

/** Título sugerido a partir da captura. */
function suggestTitle(zona: string, section: SectionId): string {
  const label = sectionLabelOf(section);
  if (zona === '') return `Nova análise — ${label}`;
  return `${MANAUS_ZONA_DISPLAY_NAME[zona as ZonaKey]} — ${label}`;
}

/** True se a seção é uma das 8 navegáveis (exclui grafo/cinema). */
function isNavigableSection(section: SectionId): boolean {
  return WORKSPACE_SECTION_OPTIONS.some((opt) => opt.id === section);
}

/** Converte o valor do select de zona ('' → undefined). */
function zonaFromValue(value: string): ZonaKey | undefined {
  return value === '' ? undefined : (value as ZonaKey);
}

/** Texto opcional trim ('' → undefined). */
function optionalText(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/** Tags marcadas no form. */
function checkedTags(form: HTMLFormElement): WorkspaceTag[] {
  return new FormData(form).getAll('tags').map((entry) => String(entry) as WorkspaceTag);
}

/** Valor de um controle nomeado do form. */
function controlValue(form: HTMLFormElement, name: string): string {
  return (form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement).value;
}

/** Define o valor de um controle nomeado do form. */
function setControlValue(form: HTMLFormElement, name: string, value: string): void {
  (form.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement).value = value;
}

/** Repinta um item de navegação conforme estiver ativo. */
function paintNavItem(item: HTMLElement): void {
  const active = item.classList.contains('active');
  item.style.background = active ? '#E6F1FB' : 'transparent';
  item.style.color = active ? '#0C447C' : 'var(--color-text-secondary)';
  item.style.fontWeight = active ? '500' : '400';
}

/** Destaca a tag ativa (contorno) ou limpa todas. */
function paintTags(active: WorkspaceTag | null): void {
  qsa<HTMLElement>('.u-ws-tag').forEach((tagEl) => {
    const isActive = tagEl.dataset.tag === active;
    tagEl.style.outline = isActive ? '2px solid var(--color-text-primary)' : 'none';
  });
}

/** Repinta um botão do toggle conforme estiver ativo. */
function paintViewButton(button: HTMLElement): void {
  const active = button.classList.contains('active');
  button.style.background = active ? 'var(--color-background-primary)' : 'transparent';
  button.style.color = active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)';
}

/** Lê um data-* obrigatório do markup (guarda defensiva inalcançável em produção). */
function requiredData(el: HTMLElement, key: string): string {
  const value = el.dataset[key];
  /* v8 ignore next 2 -- todo nó interativo do Workspace traz seu data-* no markup */
  if (value === undefined)
    throw new Error(`workspace: data-${key} ausente em ${JSON.stringify(el.className)}`);
  return value;
}
